<template>
  <div class="brands-screen" v-if="brands.length > 0">
    <div class="brands-screen__content">
      <div class="brands-screen__header container">
        <ScreenHeader
          data-aos="fade-down"
          data-aos-offset="-200"
          >
          <template #subtitle>{{
            subtitle || BRANDS_SCREEN_DEFAULTS.subtitle
          }}</template>
          <template #title>{{ title || BRANDS_SCREEN_DEFAULTS.title }}</template>
          <template #text>{{
            description || BRANDS_SCREEN_DEFAULTS.description
          }}</template>
        </ScreenHeader>
        <Button size="lg" @click="emit('button-click')">
          Let's Talk
        </Button>
      </div>

      <div
        class="brands-screen__brands-wrapper"
        data-aos="fade-up"
        data-aos-delay="200"
        @mouseenter="pauseAnimation"
        @mouseleave="resumeAnimation"
      >
        <div 
          class="brands-screen__brands brands-screen__brands--scrolling"
          :class="{ 'brands-screen__brands--paused': isPaused }"
          v-for="i in 2"
        >
          <div
            v-for="(brand, index) in duplicatedBrands"
            :key="`original-${index}`"
            class="brands-screen__brand"
          >
            <img
              :src="brand.logoUrl"
              :alt="`Brand logo ${index + 1}`"
              class="brands-screen__brand-logo"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Button from "../buttons/Button.vue";

  const BRANDS_SCREEN_DEFAULTS = {
    title: "Make your brand stand out",
    subtitle: "Brand Integration",
    description:
      "Introduce your brand to millions of players through immersive gaming experiences.",
  };

  const props = defineProps<{
    title?: string;
    subtitle?: string;
    description?: string;
    brands: { logoUrl: string }[];
  }>();
  const emit = defineEmits<{
    (e: 'button-click'): void;
  }>();

  const isPaused = ref(false);

  const duplicatedBrands = computed(() => {
    const brandsList = props.brands;
    if (brandsList.length === 0) return [];

    const multiplier = Math.max(3, Math.ceil(20 / brandsList.length));
    return Array(multiplier).fill(brandsList).flat();
  });

  const pauseAnimation = () => {
    isPaused.value = true;
  };

  const resumeAnimation = () => {
    isPaused.value = false;
  };
</script>

<style lang="scss">
  .brands-screen {
    background: linear-gradient(135deg, #111c41 0%, #182656 50%, #e43233 100%);

    padding: 128px 0;

    &__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 64px;

      @media (max-width: $md) {
        gap: 48px;
      }
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 32px;

      * {
        color: white;
      }

      @media (max-width: $md) {
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
      }
    }

    &__brands-wrapper {
      display: flex;
      width: 100%;
      overflow: hidden;
      position: relative;

      &::before,
      &::after {
        content: "";
        position: absolute;
        top: 0;
        width: 100px;
        height: 100%;
        z-index: 2;
        pointer-events: none;
      }

      &::before {
        left: 0;
        background: linear-gradient(
          to right,
          #111c41 0%,
          rgba(17, 28, 65, 0) 100%
        );
      }

      &::after {
        right: 0;
        background: linear-gradient(
          to left,
          #e43233 0%,
          rgba(228, 50, 51, 0) 100%
        );
      }
    }

    &__brands {
      display: flex;
      width: fit-content;

      &--scrolling {
        animation: infinite-scroll 30s linear infinite;
      }

      &--paused {
        animation-play-state: paused;
      }
    }

    &__brand {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 16px;

      padding: 24px;
      margin-right: 32px;
      height: 120px;
      width: 300px;

      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      }

      @media (max-width: $md) {
        height: 100px;
        width: 150px;
        padding: 20px;
        margin-right: 16px;
      }
    }

    &__brand-logo {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      opacity: 0.9;

      transition: opacity 0.3s ease;

      .brands-screen__brand:hover & {
        opacity: 1;
      }
    }
  }

  @keyframes infinite-scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-100%);
    }
  }
</style>
